import {Injectable} from '@angular/core';
import {AsyncSubject, forkJoin, Observable, of} from 'rxjs';
import {IAssignedProject, IProject, ISeekProject} from './project';
import {map, mergeMap, take} from 'rxjs/operators';
import {PermissionService} from '../../core/permission.service';
import {MIHttpClientService} from 'src/app/shared/mi-http-client.service';
import {IClient} from '../clients/client';
import {ILanguage} from '../../shared/components/language/language.model';
import {LanguageService} from '../../shared/components/language/language.service';
import {IdName} from '../../shared/common-interfaces';
import {BusinessOrgProfileService} from "../../stores/services/business-org-profile.service";
import {ContentTypesEnum, IContent} from '../../viba/admin/content/content.model';
import {CmsService} from '../../viba/agents/service/cms.service';
import {isNil} from 'lodash-es';

@Injectable({
  providedIn: 'root',
})
export class ProjectsService {

  constructor(
    private miClient: MIHttpClientService,
    public cmsService: CmsService,
    private permissionService: PermissionService,
    private languageService: LanguageService,
    private businessOrgProfileService: BusinessOrgProfileService,
  ) {
  }

  getAllProjects(): Observable<IProject[]> {
    return this.miClient.get<IProject[]>('projects').pipe(
      map(results => results.sort((a, b) => (a.projectName > b.projectName) ? 1 : -1)),
    );
  }

  getAllProjectsAsIdName(): Observable<IdName[]> {
    return this.getAllProjects().pipe(
      map(projects => projects.map(item => {
        return {
          id: item.id,
          name: item.projectName,
        }
      })),
    )
  }/**/


  getByProjectId(projectId: number): Observable<IProject> {
    return this.miClient.get<IProject>('projects/' + projectId);
  }

  geProjectNew(): Observable<IProject> {
    return this.miClient.get<IProject>('projects/new');
  }

  createProject(item: any): Observable<any> {
    return this.miClient.post('projects', item);
  }

  updateProject(item: IProject): Observable<any> {
    return this.miClient.post('projects/' + item.id, item);
  }

  deleteProject(id: number) {
    return this.miClient.delete('projects/' + id);
  }

  getNewProjectData(): Observable<[IClient[], ILanguage[], IProject]> {
    const clients$ = this.businessOrgProfileService.getClients();
    const languages$ = this.languageService.getLanguages();
    const projectNew$ = this.geProjectNew();

    return forkJoin([clients$, languages$ , projectNew$]);
  }

  getProjectDetailsData(id: number): Observable<[IProject, IClient[], ILanguage[], IContent[]]> {
    const project$ = this.getByProjectId(id);
    const clients$ = this.businessOrgProfileService.getClients();
    const languages$ = this.languageService.getLanguages();
    const backgrounds$ = this.cmsService.getProjectContentsOfType(id, ContentTypesEnum.BACKGROUND);
    return forkJoin([project$, clients$, languages$, backgrounds$]);
  }

  getSeekTenants(): Observable<ISeekProject[]> {
    return this.miClient.get<ISeekProject[]>('projects/seekTenants');
  }


  filterProjects(name: string): Observable<IProject[]> {
    return this.getAllProjects().pipe(
      map(projects => projects.filter(project => {
        if (typeof name.toLowerCase === 'function') {
          return project.projectName.toLowerCase().includes(name.toLowerCase());
        } else {
          return true;
        }
      })),
    );
  }


  public getAllProjectsWithAssignedInfo(assignedProjectsIds: number[]): Observable<{ items: IAssignedProject[] }> {
    const assignedProjectsSubject = new AsyncSubject<{ items: IAssignedProject[] }>();

    this.getAllProjects().subscribe(projects => {
        const userProjects = projects.map(project => {
          return {
            project: project,
            assigned: assignedProjectsIds && assignedProjectsIds.includes(project.id),
          };
        });

        assignedProjectsSubject.next({items: userProjects});
        assignedProjectsSubject.complete();
      },
    );

    return assignedProjectsSubject.asObservable();
  }

  public assignFormToProjects(formId: number, projectIds: number[]): Observable<any> {
    return this.miClient.post('projects/formToProjects', {
      assignedProjects: projectIds,
      formId: formId,
    })
      .pipe(
        map(() => formId),
      );
  }

  public assignStoreToProjects(storeId: number, projectIds: number[]): Observable<any> {
    return this.miClient.post('projects/storeToProjects', {
      assignedProjects: projectIds,
      storeId: storeId,
    });
  }

  getUserProjects(userId: number): Observable<IProject[]> {
    return this.getProjectsIfOnePermission(
      `projects/userToProjects/${userId}`,  'VIBA_ADMIN');
  }

  getFormProjects(formId: number): Observable<IProject[]> {
    if (formId === 0 || formId === undefined) {
      return of([]);
    }
    return this.getProjectsIfOnePermission(`projects/formToProjects/${formId}`, 'VIBA_ADMIN');
  }

  getClientProjects(clientId: number): Observable<any> {
    return this.getAllProjects()
      .pipe(
        map(projects => projects.filter(project => project.clientId === clientId)),
      );
  }

  getStoreProjects(storeId: number): Observable<IProject[]> {
    return this.getProjectsIfOnePermission(`projects/storeToProjects/${storeId}`, 'VIBA_ADMIN');
  }

  private getProjectsIfOnePermission(apiUrl: string, ...permission: string[]): Observable<IProject[]> {
    return this.permissionService.hasAtLeastOnePermission(...permission).pipe(
      take(1),
      mergeMap(hasPermission => {
        if (hasPermission) {
          return this.miClient.get<IProject[]>(apiUrl);
        } else {
          return of([]);
        }
      }),
    );
  }

  private filterAndSortContents(contents$: Observable<IContent[]>): Observable<IContent[]> {
    return contents$.pipe(
      map(contents => contents.filter(content => !isNil(content.s3Link)).sort((a, b) => a.description.localeCompare(b.description)))
    );
  }


}
